"use client";

import { useState, useTransition, useRef, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { COST_GUIDES, type CostGuide, type CostItem } from "@/app/guides/data";
import { PostcodeInput } from "@/app/components/PostcodeInput";
import { isValidPostcodeFormat } from "@/lib/postcode";
import {
  searchNearbyTradespeople,
  type NearbyTradesperson,
} from "@/app/actions/search";
import { saveEstimateResult } from "@/app/actions/estimates";
import { ShareButton } from "@/app/components/ShareButton";

const guidesPerTrade = new Map<string, CostGuide[]>();
for (const guide of COST_GUIDES) {
  const existing = guidesPerTrade.get(guide.tradeSlug) ?? [];
  existing.push(guide);
  guidesPerTrade.set(guide.tradeSlug, existing);
}

const availableTrades = Array.from(guidesPerTrade.entries()).map(
  ([slug, guides]) => ({
    slug,
    name: guides[0].tradeName,
  })
);

function stripGuideTitle(title: string): string {
  return title
    .replace(/^How Much Does? /i, "")
    .replace(/^How Much Do /i, "")
    .replace(/ Cost\??$/i, "")
    .trim();
}

function parsePriceString(s: string): number {
  return parseInt(s.replace(/[£,\s]/g, ""), 10) || 0;
}

export function CostEstimatorClient() {
  const searchParams = useSearchParams();
  // Handoff parameters seed the form once; later edits remain user controlled.
  const [initialSelection] = useState(() => {
    const tradeParam = searchParams.get("trade");
    const trade = availableTrades.some((item) => item.slug === tradeParam) ? tradeParam : null;
    const guides = trade ? guidesPerTrade.get(trade) ?? [] : [];
    const guide = guides.length === 1 ? guides[0] : null;
    const scope = searchParams.get("scope");
    const costs = guide?.costs ?? [];
    const index = scope === "small" ? 0 : scope === "large" ? costs.length - 1 : Math.floor(costs.length / 2);
    return {
      trade,
      guide,
      cost: scope ? costs[index] ?? null : null,
      postcode: searchParams.get("postcode") ?? "",
    };
  });
  const [selectedTrade, setSelectedTrade] = useState<string | null>(initialSelection.trade);
  const [selectedGuide, setSelectedGuide] = useState<CostGuide | null>(initialSelection.guide);
  const [selectedCost, setSelectedCost] = useState<CostItem | null>(initialSelection.cost);
  const [postcode, setPostcode] = useState(initialSelection.postcode);
  const [results, setResults] = useState<NearbyTradesperson[]>([]);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [estimateId, setEstimateId] = useState<string | null>(null);
  const [, startSaveTransition] = useTransition();

  const scopeRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);
  const postcodeRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const { trade, guide, cost, postcode } = initialSelection;
    if (!trade || !guide || !cost) return;
    startSaveTransition(async () => {
      const { id } = await saveEstimateResult({
        tradeCategory: trade,
        jobType: stripGuideTitle(guide.title),
        scope: cost.item,
        postcode: postcode.trim(),
        costLow: parsePriceString(cost.low),
        costHigh: parsePriceString(cost.high),
      });
      if (id) setEstimateId(id);
    });
  }, [initialSelection, startSaveTransition]);

  function scrollTo(ref: React.RefObject<HTMLDivElement | null>) {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  function handleTradeSelect(tradeSlug: string) {
    setSelectedTrade(tradeSlug);
    const guides = guidesPerTrade.get(tradeSlug) ?? [];
    if (guides.length === 1) {
      setSelectedGuide(guides[0]);
    } else {
      setSelectedGuide(null);
    }
    setSelectedCost(null);
    setResults([]);
    setSearched(false);
    setSearchError(null);
    scrollTo(scopeRef);
  }

  function handleGuideSelect(guide: CostGuide) {
    setSelectedGuide(guide);
    setSelectedCost(null);
    scrollTo(scopeRef);
  }

  function handleCostSelect(cost: CostItem) {
    setSelectedCost(cost);
    setEstimateId(null);
    scrollTo(priceRef);

    startSaveTransition(async () => {
      const { id } = await saveEstimateResult({
        tradeCategory: selectedTrade!,
        jobType: selectedGuide ? stripGuideTitle(selectedGuide.title) : "",
        scope: cost.item,
        postcode: postcode.trim(),
        costLow: parsePriceString(cost.low),
        costHigh: parsePriceString(cost.high),
      });
      if (id) setEstimateId(id);
    });
  }

  function handleSearch() {
    if (
      !selectedTrade ||
      !postcode.trim() ||
      !isValidPostcodeFormat(postcode.trim())
    )
      return;

    startTransition(async () => {
      const { error, results: data } = await searchNearbyTradespeople(
        postcode.trim(),
        selectedTrade
      );
      setSearchError(error);
      setResults(data);
      setSearched(true);
      scrollTo(resultsRef);
    });
  }

  const selectedGuides = selectedTrade
    ? (guidesPerTrade.get(selectedTrade) ?? [])
    : [];
  const hasMultipleGuides = selectedGuides.length > 1;

  const stepScope = hasMultipleGuides ? 3 : 2;
  const stepPostcode = hasMultipleGuides ? 4 : 3;
  const validPostcode = isValidPostcodeFormat(postcode.trim());
  const postJobPath = selectedTrade
    ? `/post-job?trade=${encodeURIComponent(selectedTrade)}${
        validPostcode ? `&postcode=${encodeURIComponent(postcode.trim())}` : ""
      }`
    : "/post-job";
  const homeownerSignupPath = `/signup/homeowner?next=${encodeURIComponent(
    postJobPath
  )}`;

  return (
    <div className="max-w-3xl mx-auto space-y-10">
      {/* Step 1: Trade */}
      <section>
        <StepHeader step={1} title="What trade do you need?" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {availableTrades.map((trade) => (
            <button
              key={trade.slug}
              type="button"
              onClick={() => handleTradeSelect(trade.slug)}
              className={`text-left px-4 py-3.5 rounded-md border-2 transition-all text-sm font-medium cursor-pointer ${
                selectedTrade === trade.slug
                  ? "border-forest bg-forest/5 text-forest"
                  : "border-stone/30 bg-white text-charcoal hover:border-sage-light"
              }`}
            >
              {trade.name}
            </button>
          ))}
        </div>
      </section>

      {/* Step 2 (conditional): Project type */}
      {selectedTrade && hasMultipleGuides && (
        <section>
          <StepHeader step={2} title="What type of project?" />
          <div className="space-y-2">
            {selectedGuides.map((guide) => (
              <button
                key={guide.slug}
                type="button"
                onClick={() => handleGuideSelect(guide)}
                className={`w-full text-left px-4 py-3.5 rounded-md border-2 transition-all text-sm cursor-pointer ${
                  selectedGuide?.slug === guide.slug
                    ? "border-forest bg-forest/5 text-forest font-medium"
                    : "border-stone/30 bg-white text-charcoal hover:border-sage-light"
                }`}
              >
                {stripGuideTitle(guide.title)}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Step: Scope */}
      {selectedGuide && (
        <section ref={scopeRef}>
          <StepHeader step={stepScope} title="What's the scope of work?" />
          <div className="space-y-2">
            {selectedGuide.costs.map((cost, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleCostSelect(cost)}
                className={`w-full text-left px-4 py-3.5 rounded-md border-2 transition-all text-sm cursor-pointer flex items-center justify-between gap-4 ${
                  selectedCost === cost
                    ? "border-forest bg-forest/5 text-forest font-medium"
                    : "border-stone/30 bg-white text-charcoal hover:border-sage-light"
                }`}
              >
                <span>{cost.item}</span>
                <span className="text-xs text-text-muted whitespace-nowrap">
                  {cost.low} &ndash; {cost.high}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Price estimate */}
      {selectedCost && (
        <section ref={priceRef}>
          <div className="bg-white rounded-md border border-stone/30 p-6 sm:p-8 shadow-sm">
            <h3 className="font-serif text-lg font-bold text-charcoal mb-6">
              Estimated cost: {selectedCost.item}
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="py-3">
                <p className="text-[11px] text-text-muted mb-1.5 uppercase tracking-widest font-semibold">
                  Budget
                </p>
                <p className="text-xl font-bold text-sage tabular-nums">
                  {selectedCost.low}
                </p>
              </div>
              <div className="bg-forest/5 rounded-lg py-3">
                <p className="text-[11px] text-forest mb-1.5 uppercase tracking-widest font-semibold">
                  Typical
                </p>
                <p className="text-2xl font-bold text-forest tabular-nums">
                  {selectedCost.mid}
                </p>
              </div>
              <div className="py-3">
                <p className="text-[11px] text-text-muted mb-1.5 uppercase tracking-widest font-semibold">
                  Premium
                </p>
                <p className="text-xl font-bold text-sage tabular-nums">
                  {selectedCost.high}
                </p>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-5 text-center">
              {selectedGuide?.vatNote ??
                "Prices are UK averages for 2026 including VAT where applicable."}
            </p>
            <div className="mt-4 flex items-center justify-center gap-4">
              {selectedGuide && (
                <Link
                  href={`/guides/${selectedGuide.slug}`}
                  className="text-xs font-semibold text-forest hover:text-forest-light no-underline transition-colors"
                >
                  Read the full {selectedGuide.tradeName.toLowerCase()} cost
                  guide &rarr;
                </Link>
              )}
              {estimateId && (
                <ShareButton
                  url={`/cost-estimator/${estimateId}`}
                  title={`${selectedCost?.item} Cost Estimate | Tradesfolk`}
                  text={`${selectedCost?.item}: ${selectedCost?.low} to ${selectedCost?.high}. Get your own estimate at tradesfolk.co`}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {selectedCost && selectedTrade && (
        <section
          className="rounded-md border border-sage-light/60 bg-sage-pale p-5 sm:p-6"
          aria-labelledby="estimate-next-step"
        >
          <h2
            id="estimate-next-step"
            className="font-serif text-xl font-bold text-charcoal"
          >
            Ready to turn this estimate into a real job?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-text-body">
            Create a free homeowner account to post the work, add photos and
            start conversations. Tradesfolk is just launching, so nearby choice
            and response volume will vary.
          </p>
          <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Link
              href={homeownerSignupPath}
              className="inline-flex min-h-11 items-center justify-center rounded-sm bg-forest px-5 py-3 text-sm font-semibold text-white no-underline transition-colors hover:bg-forest-light"
            >
              Create free homeowner account
            </Link>
            <Link
              href={postJobPath}
              className="inline-flex min-h-11 items-center text-sm font-semibold text-forest no-underline hover:text-forest-light"
            >
              Already a member? Post this job
            </Link>
          </div>
        </section>
      )}

      {/* Step: Postcode */}
      {selectedGuide && (
        <section ref={postcodeRef}>
          <StepHeader
            step={stepPostcode}
            title={`Find ${selectedGuide.tradeName.toLowerCase()}s near you`}
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <PostcodeInput
                value={postcode}
                onChange={setPostcode}
                placeholder="e.g. SW1A 1AA"
                ariaLabel="Your postcode"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              disabled={
                isPending ||
                !postcode.trim() ||
                !isValidPostcodeFormat(postcode.trim())
              }
              className="px-6 py-3 bg-forest text-white font-semibold rounded-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-forest-light transition-colors shrink-0 h-[46px]"
            >
              {isPending ? "Searching..." : "Search"}
            </button>
          </div>
        </section>
      )}

      {/* Results */}
      {searched && (
        <section ref={resultsRef}>
          {searchError && (
            <p className="text-sm text-error mb-4">{searchError}</p>
          )}
          {results.length > 0 ? (
            <>
              <h2 className="font-serif text-xl font-bold text-charcoal mb-4">
                {selectedGuide?.tradeName}s near {postcode}
              </h2>
              <div className="space-y-3">
                {results.map((person) => (
                  <Link
                    key={person.user_id}
                    href={`/tradesperson/${person.user_id}`}
                    className="flex items-center gap-4 bg-white rounded-md border border-stone/30 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all no-underline group"
                  >
                    {person.photo_url ? (
                      <Image
                        src={person.photo_url}
                        alt={`${person.first_name} ${person.last_name}`}
                        width={48}
                        height={48}
                        className="rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-cream flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-forest">
                          {person.first_name?.charAt(0)}
                          {person.last_name?.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-charcoal text-sm group-hover:text-forest transition-colors">
                        {person.first_name} {person.last_name}
                        {person.business_name && (
                          <span className="text-text-muted font-normal">
                            {" "}
                            &middot; {person.business_name}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {person.review_count > 0 && (
                          <span className="text-xs text-text-muted">
                            <span className="text-copper">&#9733;</span>{" "}
                            {person.avg_rating.toFixed(1)} (
                            {person.review_count} review
                            {person.review_count !== 1 ? "s" : ""})
                          </span>
                        )}
                        {person.distance_miles != null && (
                          <span className="text-xs text-text-muted">
                            {person.distance_miles.toFixed(1)} miles away
                          </span>
                        )}
                        {person.years_experience != null &&
                          person.years_experience > 0 && (
                            <span className="text-xs text-text-muted">
                              {person.years_experience} yr
                              {person.years_experience !== 1 ? "s" : ""} exp
                            </span>
                          )}
                      </div>
                    </div>
                    <svg
                      className="w-5 h-5 text-text-muted shrink-0 group-hover:text-forest transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  href={`/browse?trade=${selectedTrade}&postcode=${encodeURIComponent(postcode)}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-forest-light no-underline transition-colors"
                >
                  View all {selectedGuide?.tradeName.toLowerCase()}s near{" "}
                  {postcode}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </>
          ) : (
            !searchError && (
              <div className="text-center py-10 bg-white rounded-md border border-stone/30">
                <p className="text-text-muted mb-4">
                  No {selectedGuide?.tradeName.toLowerCase()}s found near{" "}
                  {postcode} yet.
                </p>
                <Link
                  href={`/post-job?trade=${selectedTrade}&postcode=${encodeURIComponent(postcode)}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-forest text-white font-semibold rounded-sm text-sm no-underline hover:bg-forest-light transition-colors"
                >
                  Post your job instead
                </Link>
              </div>
            )
          )}
        </section>
      )}

      {/* Cross-links (shown after estimate result) */}
      {selectedCost && (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
          <Link
            href={`/prices/${selectedTrade}`}
            className="p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group text-center"
          >
            <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Full Price Index</p>
            <p className="text-xs text-text-muted mt-1">All {selectedGuide?.tradeName.toLowerCase()} job types and rates</p>
          </Link>
          <Link
            href={`/compare?trade=${selectedTrade}`}
            className="p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group text-center"
          >
            <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Compare Cities</p>
            <p className="text-xs text-text-muted mt-1">Side-by-side cost comparison</p>
          </Link>
          <Link
            href={`/snap${postcode ? `?postcode=${encodeURIComponent(postcode)}` : ""}`}
            className="p-4 bg-white rounded-md border border-stone/30 no-underline hover:border-sage-light transition-colors group text-center"
          >
            <p className="text-sm font-semibold text-charcoal group-hover:text-forest transition-colors">Snap and Fix</p>
            <p className="text-xs text-text-muted mt-1">AI photo diagnosis</p>
          </Link>
        </section>
      )}
    </div>
  );
}

function StepHeader({ step, title }: { step: number; title: string }) {
  return (
    <div className="mb-4">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-forest text-white text-xs font-bold mb-2">
        {step}
      </span>
      <h2 className="font-serif text-xl font-bold text-charcoal">{title}</h2>
    </div>
  );
}
